/**
 * Account Service
 * --------------------
 * Provides functions to interact with account-related API endpoints.
 * Includes CRUD-like operations and role fetching.
 *
 * @author  Harvinder Singh
 * @created 2025-09-12
 */

import { ref, Ref } from 'vue'
import { UnificationHttpClient } from '@/lib/repositories'
import type { AxiosResponse } from 'axios'

// --- API Resource ---
const RESOURCE = '/accounts.json'

// --- Types ---
export interface Account {
  id: number
  name: string
  [key: string]: unknown
}

export interface NetoAccount {
  id: string
  name: string
  path: string
  url: string
}

export interface Role {
  id: number
  name: string
  [key: string]: unknown
}

export interface Pagination {
  itemsPerPage: number
  page: number
  sortBy: string[]
  totalItems: number
}

export interface Provisioning {
  is_free_trial_lead: boolean
  status: string
}

export interface ApiResponse<T> {
  data: T
  meta?: {
    total: number
    [key: string]: unknown
  }
}

export type ValueType = 'limit' | 'boolean';

export interface FeatureConfig {
  created_at: string;
  updated_at: string;
  display_name: string;
  feature_key: string;
  package_name: string;
  value: string;
  value_type: ValueType;
}

export interface PackageFeature {
  product_name: string;
  renewal_date: string;
  features: FeatureConfig[];
  start_date: string;
  subscription_type: string;
  package_name?: string;
  account_package_id?: string;
  currency_code?: string;
  status?: string;
}

export interface AccountRequestPayload {
  product_type: string
}

/**
 * useAccountService
 * --------------------
 * Provides reactive state and methods for account operations.
 */
export function useAccountService() {
  const httpClient = UnificationHttpClient
  const isFetching = ref(false)

  // Reactive state
  const data = {
    items: ref<Account[]>([]),
    item: ref<Account | null>(null),
    roles: ref<Role[]>([]),
    netoAccounts: ref<NetoAccount[]>([])
  }

  const pagination: Ref<Pagination> = ref({
    itemsPerPage: 10,
    page: 1,
    sortBy: [],
    totalItems: 10
  })

  const provisioning: Ref<Provisioning> = ref({
    is_free_trial_lead: false,
    status: ''
  })

  /**
   * Fetch all accounts
   * @param config Optional Axios config (filters, pagination)
   * @returns AxiosResponse with Account array
   */
  async function findAll(config: Record<string, unknown> = {}): Promise<AxiosResponse<ApiResponse<Account[]>>> {
    isFetching.value = true
    try {
      const response = await httpClient.get<ApiResponse<Account[]>>(RESOURCE, config)
      const payload = response.data

      data.items.value = payload.data ? payload.data : []
      pagination.value.totalItems = payload.meta?.total ?? payload.data.length
      provisioning.value = payload.meta?.provisioning as Provisioning ?? { is_free_trial_lead: false, status: '' }

      return response
    } finally {
      isFetching.value = false
    }
  }

  /**
   * Fetch a single account
   * @param config Optional Axios config
   * @returns AxiosResponse with single Account
   */
  async function find(config: Record<string, unknown> = {}): Promise<AxiosResponse<ApiResponse<Account>>> {
    const response = await httpClient.get<ApiResponse<Account>>(RESOURCE, config)
    data.item.value = response.data.data
    return response
  }

  /**
   * Fetch roles for the current account
   * @param config Optional Axios config
   * @returns AxiosResponse with Role array
   */
  async function fetchRoles(config: Record<string, unknown> = {}): Promise<AxiosResponse<ApiResponse<Role[]>>> {
    // Dynamically import store to avoid circular dependency issues
    const { useUserStore } = await import('@/lib/stores')
    const userStore = useUserStore()
    const rolesResource = `/accounts/${userStore.getAccountId}/members/roles.json`

    const response = await httpClient.get<ApiResponse<Role[]>>(rolesResource, config)
    data.roles.value = response.data.data

    return response
  }

  async function fetchPackageFeatures(config: Record<string, unknown> = {}): Promise<AxiosResponse<ApiResponse<Record<string, PackageFeature>>>> {
    const { useUserStore } = await import('@/lib/stores')
    const userStore = useUserStore()
    const packageFeaturesResource = `/accounts/${userStore.getAccountId}/package_features.json`

    const response = await httpClient.get<ApiResponse<Record<string, PackageFeature>>>(packageFeaturesResource, config)
    return response;
  }

  async function sendRequest(payload: AccountRequestPayload,config: Record<string, unknown> = {}): Promise<AxiosResponse<ApiResponse<unknown>>> {
    return await httpClient.post<ApiResponse<unknown>>(`/requests.json`,payload,config)
  }

  /**
   * Fetch Neto accounts for the current account
   * @param config Optional Axios config
   * @returns AxiosResponse with any array
   */
  async function fetchNetoAccount(config: Record<string, unknown> = {}): Promise<AxiosResponse<ApiResponse<NetoAccount[]>>> {
    const netoResource = 'users/groups'
    const response = await httpClient.get<ApiResponse<NetoAccount[]>>(netoResource, config)
    data.netoAccounts.value = response.data.data
    return response
  }

  // Public API
  return {
    isFetching,
    pagination,
    provisioning,
    ...data,
    findAll,
    find,
    fetchRoles,
    fetchNetoAccount,
    fetchPackageFeatures,
    sendRequest
  }
}

export const $accountService = useAccountService()
